// Kartu Mockup Offline (tim Mockup/Mia): ringkasan produk, status mockup, menu Lainnya. UI saja — data & aksi dari kartu induk.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Shirt, Layers, MoreHorizontal, ChevronDown, ChevronRight, Undo2, AlertTriangle, ClipboardPaste } from "lucide-react";

const qtyOf = (p) => Number(p.quantity || 0) || (Array.isArray(p.sizes) ? p.sizes.reduce((s, x) => s + Number(x.quantity || 0), 0) : 0);
export const mockupDone = (p) => !!(p?.has_mockup || p?.linked_mockup_valid || p?.follows_note_mockup);

export function MockupSummaryBox({ productCards, totalPcs, fmtSizes, testid }) {
  const isGroup = productCards.length > 1;
  const p = productCards[0].product;
  if (!isGroup) {
    const sizes = fmtSizes(p);
    const sub = [p.color, sizes && `Uk: ${sizes}`].filter(Boolean).join(" · ");
    return <div className="mo-box" data-testid={testid}>
      <Shirt size={22} className="mo-box-icon" aria-hidden="true"/>
      <div className="mo-box-main">
        <strong className="mo-box-title">{p.custom_name || p.name || "Produk"}</strong>
        {sub && <span className="mo-box-sub">{sub}</span>}
      </div>
      <div className="mo-box-qty"><strong>{qtyOf(p)} pcs</strong></div>
    </div>;
  }
  const names = productCards.map(pc => pc.product.custom_name || pc.product.name || "Produk");
  return <div className="mo-box" data-testid={testid}>
    <Layers size={22} className="mo-box-icon" aria-hidden="true"/>
    <div className="mo-box-main">
      <strong className="mo-box-title">{productCards.length} produk</strong>
      <span className="mo-box-sub">{names[0]}{names.length > 1 ? ", dll" : ""}</span>
    </div>
    <div className="mo-box-qty"><span className="mo-box-qty-cap">Total</span><strong>{totalPcs} pcs</strong></div>
  </div>;
}

export function MockupProductList({ productCards, fmtSizes, testid }) {
  return <ul className="mo-prod-list" data-testid={testid}>
    {productCards.map((pc, i) => {
      const p = pc.product, sz = fmtSizes(p);
      return <li key={p.id || i} data-testid={`${testid}-${p.id || i}`}>
        <strong>{p.custom_name || p.name || "Produk"}</strong> · {qtyOf(p)} pcs
        {p.color && <span className="mo-muted"> · {p.color}</span>}
        {sz && <span className="mo-muted"> · {sz}</span>}
        {mockupDone(p) ? <span className="mo-tag mo-tag-ok">Mockup ada</span> : <span className="mo-tag">Belum</span>}
      </li>;
    })}
  </ul>;
}

export function MockupStatusRow({ targets, isGroup, open, onToggle, testid }) {
  const n = targets.length, done = targets.filter(pc => mockupDone(pc.product)).length;
  let label, tone;
  if (n <= 1) { label = done ? "Sudah Diunggah" : "Belum Diunggah"; tone = done ? "ok" : "none"; }
  else { label = `${done} dari ${n}`; tone = done === n ? "ok" : done ? "part" : "zero"; }
  return <div className="mo-status" onClick={e => e.stopPropagation()}>
    <span className="mo-status-cap">Mockup:</span>
    <span className={`mo-chip mo-chip-${tone}`} data-testid={`${testid}-status`}>{label}</span>
    {isGroup && <button type="button" className="mo-link" onClick={onToggle} aria-expanded={open} data-testid={`${testid}-toggle`}>
      {open ? "Tutup Produk" : "Lihat Produk"} {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
    </button>}
  </div>;
}

export function MockupMoreMenu({ canRelease, onRelease, onReport, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const pick = (fn) => { setOpen(false); fn(); };
  return <div className="mo-more" ref={ref} onClick={e => e.stopPropagation()}>
    <button type="button" className="soft-btn small-btn mo-more-btn" aria-haspopup="menu" aria-expanded={open}
      onClick={() => setOpen(v => !v)} data-testid={`${testid}-more`}>
      <MoreHorizontal size={16} aria-hidden="true"/> Lainnya <ChevronDown size={14} aria-hidden="true"/>
    </button>
    {open && <div className="mo-more-menu" role="menu" data-testid={`${testid}-more-menu`}>
      {canRelease && <button type="button" role="menuitem" onClick={() => pick(onRelease)} data-testid={`${testid}-more-release`}>
        <Undo2 size={15} aria-hidden="true"/> Kembalikan ke Antrean</button>}
      <button type="button" role="menuitem" onClick={() => pick(onReport)} data-testid={`${testid}-more-report`}>
        <AlertTriangle size={15} aria-hidden="true"/> Laporkan Kendala</button>
    </div>}
  </div>;
}

const pickClipImage = (items) => {
  for (const it of items || []) if (it.kind === "file" && String(it.type || "").startsWith("image/")) { const f = it.getAsFile(); if (f) return f; }
  return null;
};
const named = (b) => new File([b], `paste-${Date.now()}.${(b.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: b.type || "image/png" });

// Paste Mockup: Ctrl+V (CorelDRAW / clipboard Windows) atau tombol Tempel → pratinjau → pilih tujuan → simpan lewat alur unggah kartu.
export function PasteMockupModal({ orderNumber, targets, onClose, onSave }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  const [target, setTarget] = useState(targets.length > 1 ? "" : "__all__");
  const [busy, setBusy] = useState(false);
  const take = (b) => { setErr(""); setFile(named(b)); setUrl(u => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(b); }); };
  useEffect(() => {
    // Capture di window: modal ini yang menerima tempel selama terbuka (tidak diteruskan ke penempel lain).
    const onPaste = (e) => {
      const b = pickClipImage(e.clipboardData?.items);
      e.stopImmediatePropagation();
      if (!b) { setErr("Clipboard tidak berisi gambar. Salin gambar di CorelDRAW (Ctrl+C), lalu tekan Ctrl+V di sini."); return; }
      e.preventDefault(); take(b);
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, []);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const fromClipboard = async () => {
    setErr("");
    try {
      if (!navigator.clipboard?.read) throw Object.assign(new Error(""), { name: "Unsupported" });
      for (const it of await navigator.clipboard.read()) {
        const t = (it.types || []).find(x => x.startsWith("image/"));
        if (t) { const b = await it.getType(t); take(new File([b], "clip", { type: t })); return; }
      }
      setErr("Clipboard tidak berisi gambar. Salin gambar dulu, lalu tempel.");
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Izin clipboard ditolak. Tekan Ctrl+V di jendela ini." : "Browser tidak bisa membaca clipboard. Tekan Ctrl+V di jendela ini.");
    }
  };
  const save = async () => {
    setBusy(true); setErr("");
    const ok = await onSave(file, target === "__all__" ? null : target);
    setBusy(false);
    if (ok) onClose();
  };
  const multi = targets.length > 1;
  return createPortal(<div className="mo-paste-backdrop" onClick={e => { e.stopPropagation(); if (!busy) onClose(); }} data-testid={`mo-paste-modal-${orderNumber}`}>
    <div className="mo-paste-panel" role="dialog" aria-modal="true" aria-label="Paste Mockup" onClick={e => e.stopPropagation()}>
      <header className="mo-paste-head"><b>Paste Mockup · {orderNumber}</b>
        <button type="button" className="mo-paste-x" onClick={onClose} disabled={busy} aria-label="Tutup" data-testid={`mo-paste-close-${orderNumber}`}>×</button></header>
      {url ? <img src={url} alt="Pratinjau mockup" className="mo-paste-img" data-testid={`mo-paste-preview-${orderNumber}`}/>
        : <div className="mo-paste-zone" data-testid={`mo-paste-zone-${orderNumber}`}>
            <ClipboardPaste size={26} aria-hidden="true"/><b>Tekan Ctrl+V untuk menempel gambar</b>
            <span>Salin gambar di CorelDRAW atau aplikasi lain, lalu tempel di sini. Pratinjau muncul sebelum disimpan.</span>
          </div>}
      {multi && <fieldset className="mo-paste-targets" data-testid={`mo-paste-targets-${orderNumber}`}>
        <legend>Simpan untuk:</legend>
        <label><input type="radio" name={`mo-t-${orderNumber}`} checked={target === "__all__"} onChange={() => setTarget("__all__")} data-testid={`mo-paste-target-all-${orderNumber}`}/>
          Semua {targets.length} produk (1 mockup gabungan, sama seperti Unggah Mockup)</label>
        {targets.map(pc => <label key={pc.product.id}><input type="radio" name={`mo-t-${orderNumber}`} checked={target === pc.product.id}
          onChange={() => setTarget(pc.product.id)} data-testid={`mo-paste-target-${pc.product.id}`}/>
          Hanya {pc.product.custom_name || pc.product.name || "Produk"} · {pc.product.quantity || 0} pcs</label>)}
      </fieldset>}
      {err && <div className="vce-err" role="alert" data-testid={`mo-paste-error-${orderNumber}`}>{err}</div>}
      <div className="mo-paste-actions">
        <button type="button" className="soft-btn" onClick={fromClipboard} disabled={busy} data-testid={`mo-paste-clipboard-${orderNumber}`}>
          <ClipboardPaste size={15} aria-hidden="true"/> {file ? "Tempel Ulang" : "Tempel dari Clipboard"}</button>
        <button type="button" className="primary" onClick={save} disabled={busy || !file || !target} data-testid={`mo-paste-save-${orderNumber}`}>
          {busy ? "Menyimpan…" : "Simpan Mockup"}</button>
      </div>
      {multi && file && !target && <div className="mo-muted mo-paste-hint">Pilih produk tujuan dulu agar gambar tidak masuk ke produk lain.</div>}
    </div>
  </div>, document.body);
}

// Nomor WA nota: 08… / +62… / 62… / 8… → 628… untuk wa.me saja (data asli tidak diubah). Tidak valid → tidak ditebak.
export const waLink = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (d.startsWith("8")) d = "62" + d;
  return /^628\d{7,11}$/.test(d) ? d : "";
};

export function MockupWaLine({ wa, testid }) {
  const [copied, setCopied] = useState(false);
  const num = waLink(wa), shown = String(wa || "").trim();
  if (!num) return <span className="mo-wa mo-wa-none" data-testid={`${testid}-none`}>Nomor WA belum tersedia</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(shown); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt("Salin nomor WA:", shown); }
  };
  return <span className="mo-wa" onClick={e => e.stopPropagation()} data-testid={testid}>
    <span className="mo-wa-num" data-testid={`${testid}-num`}>{shown}</span>
    <a className="mo-wa-btn mo-wa-chat" href={`https://wa.me/${num}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-chat`}>Chat WA</a>
    <button type="button" className="mo-wa-btn" onClick={copy} data-testid={`${testid}-copy`}>{copied ? "Tersalin" : "Salin"}</button>
  </span>;
}
